import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import i18next from 'i18next'

import { error, success } from 'src/components/system_wide/notification'
import { t_caps } from 'src/lib/constants'
import axios from 'src/lib/axios'

const { t } = i18next

const initialState = {
  total: 0,
  users: [],
  all_users: [],
  show_form: false,
  errors: {},
  current_user: {
    id: null,
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    enabled: true,
    is_management_account: false,
    business_role_id: null,
    caps: t_caps,
    parent_user: null,
    areas_competence: [],
    brands: [],
    sites: [],
    user_data: {
      business_name: '',
      pec: '',
      email: '',
      phone: '',
      piva: '',
      cf: '',
      cuu: '',
      address: '',
      city: '',
      province: '',
      cap: '',
      country: '',
      place_id: '',
    },
  },
  total_targets: 0,
  targets: [],
  total_closed_contracts: 0,
  closed_contracts: [],
  competence_areas: [],
}

export const getAllCompentenceAreas = createAsyncThunk('users/competenceAreas', async thunkApi => {
  try {
    let response = await axios({
      url: `/api/sites/aree`,
    })

    return response.data
  } catch (err) {
    error(t('store.users.get_sites_error', 'Impossibile recuperare lista delle aree di competenza'))
    return thunkApi.rejectWithValue()
  }
})

export const getTargets = createAsyncThunk(
  'users/targetList',
  async ({ offset, limit, sortData, filter, user_id }, thunkApi) => {
    try {
      let { total_targets: currentTotal, current_user } = thunkApi.getState().users
      if (offset > currentTotal) return

      let { sort_column, sort_direction } = sortData
      let response = await axios({
        url: `/api/users/targets/${user_id || current_user.id}`,
        params: { offset, limit, sort_column, sort_direction, filter },
      })
      let { total, targets } = response.data
      return { total, targets, reset: offset === 0 }
    } catch (err) {
      error(t('store.users.get_targets_error', 'Impossibile recuperare lista obiettivi'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const getClosedContracts = createAsyncThunk(
  'users/closedContractList',
  async ({ offset, limit, sortData, filter, user_id }, thunkApi) => {
    try {
      let { total_closed_contracts: currentTotal, current_user } = thunkApi.getState().users
      if (offset > currentTotal) return

      let { sort_column, sort_direction } = sortData
      let response = await axios({
        url: `/api/users/closed_contracts/${user_id || current_user.id}`,
        params: { offset, limit, sort_column, sort_direction, filter },
      })
      let { total, closed_contracts } = response.data
      return { total, closed_contracts, reset: offset === 0 }
    } catch (err) {
      error(t('store.users.get_closed_contracts_error', 'Impossibile recuperare lista contratti'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const getUsers = createAsyncThunk(
  'users/list',
  async ({ offset, limit, sortData, filter, filtered_areas, filtered_roles, filtered_brands }, thunkApi) => {
    try {
      let { total: currentTotal } = thunkApi.getState().users
      if (offset > currentTotal) return

      let { sort_column, sort_direction } = sortData
      let response = await axios({
        url: `/api/users`,
        params: {
          offset,
          limit,
          sort_column,
          sort_direction,
          filter,
          filtered_areas: filtered_areas.length === 20 ? 'all' : filtered_areas.join(','),
          filtered_roles: filtered_roles.join(','),
          filtered_brands: filtered_brands.join(','),
        },
      })
      let { total, users } = response.data
      return { total, users, reset: offset === 0 }
    } catch (err) {
      error(t('store.users.get_users_error', 'Impossibile recuperare lista utenti'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const createUser = createAsyncThunk('users/create', async ({ user }, thunkApi) => {
  try {
    await axios({
      url: `/api/users`,
      method: 'post',
      data: { ...user },
    })
    success(t('store.users.create_user_success', 'Utente creato con successo'))
    thunkApi.dispatch(resetCurrentUser())
    thunkApi.dispatch(toggleShowForm())
  } catch (err) {
    return thunkApi.rejectWithValue(err?.response?.data ?? {})
  }
})

export const updateUser = createAsyncThunk('users/update', async ({ user }, thunkApi) => {
  try {
    await axios({
      url: `/api/users/${user.id}`,
      method: 'patch',
      data: { ...user },
    })
    success(t('store.users.update_user_success', 'Utente aggiornato con successo'))
    thunkApi.dispatch(resetCurrentUser())
    thunkApi.dispatch(toggleShowForm())
  } catch (err) {
    return thunkApi.rejectWithValue(err?.response?.data ?? {})
  }
})

export const saveTarget = createAsyncThunk('users/saveTarget', async ({ target }, thunkApi) => {
  try {
    await axios({
      url: `/api/users/target`,
      method: 'PATCH',
      data: { ...target },
    })
    success(t('store.users.save_target_success', 'Obiettivo salvato con successo'))
  } catch (err) {
    error(t('store.users.save_target_error', "Impossibile salvare l'obiettivo"))
    thunkApi.dispatch(resetCurrentUser())
    return thunkApi.rejectWithValue()
  }
})
export const saveClosedContract = createAsyncThunk(
  'users/saveClosedContract',
  async ({ closed_contract }, thunkApi) => {
    try {
      await axios({
        url: `/api/users/closed_contract`,
        method: 'PATCH',
        data: { ...closed_contract },
      })
      success(t('store.users.save_closed_contract_success', 'Contratto salvato con successo'))
    } catch (err) {
      error(t('store.users.save_closed_closed_contract_error', 'Impossibile salvare il contratto'))
      return thunkApi.rejectWithValue(err?.response?.data ?? {})
    }
  }
)

export const deleteClosedContract = createAsyncThunk('users/deleteClosedContract', async ({ id }, thunkApi) => {
  try {
    await axios({ url: `/api/users/closed_contract/${id}`, method: 'delete' })
    success(t('store.users.delete_closed_contract_success', 'Contratto eliminato con successo'))
  } catch (err) {
    error(t('store.users.delete_closed_contract_error', 'Impossibile eliminare il contratto'))
    return thunkApi.rejectWithValue()
  }
})

export const deleteTarget = createAsyncThunk('users/deleteTarget', async ({ id }, thunkApi) => {
  try {
    await axios({ url: `/api/users/target/${id}`, method: 'delete' })
    success(t('store.users.delete_target_success', 'Obiettivo eliminato con successo'))
  } catch (err) {
    error(t('store.users.delete_target_error', "Impossibile eliminare l'obiettivo utente"))
    return thunkApi.rejectWithValue()
  }
})

export const deleteUser = createAsyncThunk('users/delete', async ({ id }, thunkApi) => {
  try {
    await axios({ url: `/api/users/${id}`, method: 'delete' })
    success(t('store.users.delete_user_success', 'Utente eliminato con successo'))
    thunkApi.dispatch(resetCurrentUser())
  } catch (err) {
    error(t('store.users.delete_user_error', 'Impossibile eliminare utente'))
    return thunkApi.rejectWithValue()
  }
})

export const getAllUsers = createAsyncThunk('users/getAllUsers', async ({ filtered_roles_priority = [] }, thunkApi) => {
  try {
    let response = await axios({ url: `/api/users/all`, method: 'GET', params: { filtered_roles_priority } })
    return response.data.users
  } catch (err) {
    error(t('store.users.get_users_error', 'Impossibile recuperare lista utenti'))
    return thunkApi.rejectWithValue()
  }
})

export const usersSlice = createSlice({
  name: 'users',
  initialState,

  reducers: {
    setCurrentUser: (state, action) => {
      let {
        id,
        username,
        first_name,
        last_name,
        email,
        enabled,
        is_management_account,
        business_role_id,
        caps,
        user_data,
        parent_user,
        areas_competence,
        brands,
      } = action.payload
      state.current_user.id = id
      state.current_user.username = username
      state.current_user.email = email
      state.current_user.first_name = first_name
      state.current_user.last_name = last_name
      state.current_user.enabled = enabled
      state.current_user.is_management_account = is_management_account
      state.current_user.business_role_id = business_role_id
      state.current_user.caps = caps
      state.current_user.user_data = user_data ?? initialState.current_user.user_data
      state.current_user.parent_user = parent_user?.id ? parent_user : null
      state.current_user.areas_competence = areas_competence
      state.current_user.brands = brands
    },
    updateErrors: (state, action) => {
      state.errors = action.payload.errors
    },
    toggleShowForm: state => {
      state.show_form = !state.show_form
    },
    resetCurrentUser: state => {
      state.current_user = initialState.current_user
    },
    resetUsers: () => initialState,
    resetTargets: state => {
      state.targets = initialState.targets
      state.total_targets = initialState.total_targets
    },
    resetClosedContracts: state => {
      state.closed_contracts = initialState.closed_contracts
      state.total_closed_contracts = initialState.total_closed_contracts
    },
  },

  extraReducers: {
    [createUser.rejected]: (state, action) => {
      state.errors = action.payload
    },
    [updateUser.rejected]: (state, action) => {
      state.errors = action.payload
    },
    [getAllCompentenceAreas.fulfilled]: (state, action) => {
      if (!action?.payload) return
      state.competence_areas = action.payload.aree
    },
    [getUsers.fulfilled]: (state, action) => {
      if (!action?.payload) return
      let { total, users } = action.payload
      state.total = total
      users = users.map(u => ({
        ...u,
        caps: JSON.parse(u?.caps ?? '{}'),
        brands: (u?.brands ?? []).map(b => b.id),
        areas_competence: (u?.areas_competence ?? []).map(ac => ({ value: ac.value, label: ac.label })),
        user_data: JSON.parse(u?.user_data ?? '{}'),
        parent_user: JSON.parse(u?.parent_user ?? null),
      }))
      state.users = action.payload.reset ? [...users] : [...state.users, ...users]
    },
    [getUsers.rejected]: state => {
      state.total = initialState.total
      state.users = initialState.users
    },
    [getTargets.fulfilled]: (state, action) => {
      if (!action?.payload) return
      let { total, targets } = action.payload
      state.total_targets = total
      state.targets = action.payload.reset ? [...targets] : [...state.targets, ...targets]
    },
    [getTargets.rejected]: state => {
      state.total_targets = initialState.total_targets
      state.targets = initialState.targets
    },
    [getClosedContracts.fulfilled]: (state, action) => {
      if (!action?.payload) return
      let { total, closed_contracts } = action.payload
      state.total_closed_contracts = total
      state.closed_contracts = action.payload.reset
        ? [...closed_contracts]
        : [...state.closed_contracts, ...closed_contracts]
    },
    [getClosedContracts.rejected]: state => {
      state.total_closed_contracts = initialState.total_closed_contracts
      state.closed_contracts = initialState.closed_contracts
    },
    [saveClosedContract.rejected]: (state, action) => {
      state.errors = action.payload
    },
    [getAllUsers.fulfilled]: (state, action) => {
      state.all_users = action.payload
    },
    [getAllUsers.rejected]: state => {
      state.all_users = initialState.all_users
    },
  },
})

export const {
  setCurrentUser,
  updateErrors,
  resetCurrentUser,
  toggleShowForm,
  resetUsers,
  resetTargets,
  resetClosedContracts,
} = usersSlice.actions
export default usersSlice.reducer
